/**
 * Safe localStorage wrapper that handles SSR and edge cases
 * Prevents "localStorage.getItem is not a function" errors
 */

// Check if we're in a browser environment - do this check ONCE at module load
const IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined';

// Safe check for localStorage availability
const hasLocalStorage = () => {
  if (!IS_BROWSER) return false;
  
  try {
    const storage = window.localStorage;
    if (!storage) return false;
    if (typeof storage.getItem !== 'function') return false;
    if (typeof storage.setItem !== 'function') return false;
    
    // Test that it actually works
    const testKey = '__test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// Cache the result
let _hasLocalStorage = null;
const canUseLocalStorage = () => {
  if (_hasLocalStorage === null) {
    _hasLocalStorage = hasLocalStorage();
  }
  return _hasLocalStorage;
};

export const storage = {
  getItem: (key) => {
    if (!IS_BROWSER) return null;
    if (!canUseLocalStorage()) return null;
    
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },

  setItem: (key, value) => {
    if (!IS_BROWSER) return false;
    if (!canUseLocalStorage()) return false;
    
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  },

  removeItem: (key) => {
    if (!IS_BROWSER) return false;
    if (!canUseLocalStorage()) return false;
    
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  },

  clear: () => {
    if (!IS_BROWSER) return false;
    if (!canUseLocalStorage()) return false;
    
    try {
      window.localStorage.clear();
      return true;
    } catch (e) {
      return false;
    }
  },

  keys: () => {
    if (!IS_BROWSER) return [];
    if (!canUseLocalStorage()) return [];
    
    try {
      return Object.keys(window.localStorage);
    } catch (e) {
      return [];
    }
  }
};

// Session storage wrapper
export const sessionStorage = {
  getItem: (key) => {
    if (!IS_BROWSER) return null;
    
    try {
      if (typeof window.sessionStorage?.getItem !== 'function') return null;
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },

  setItem: (key, value) => {
    if (!IS_BROWSER) return false;
    
    try {
      if (typeof window.sessionStorage?.setItem !== 'function') return false;
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  },

  removeItem: (key) => {
    if (!IS_BROWSER) return false;
    
    try {
      if (typeof window.sessionStorage?.removeItem !== 'function') return false;
      window.sessionStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  },

  clear: () => {
    if (!IS_BROWSER) return false;
    
    try {
      if (typeof window.sessionStorage?.clear !== 'function') return false;
      window.sessionStorage.clear();
      return true;
    } catch (e) {
      return false;
    }
  }
};

export default storage;
